import React from 'react';
import { AlertTriangle, ChevronRight, Loader2, Video } from 'lucide-react';

import { Badge, type BadgeProps } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { ErrorState } from '@/shared/errors';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';

import type {
  PunchWalkthroughSession,
  PunchWalkthroughSummary,
  PunchWalkthroughStatus,
} from '../types';

interface WalkthroughDraftsPanelProps {
  sessions?: (PunchWalkthroughSummary | PunchWalkthroughSession)[];
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  count?: number;
  page?: number;
  hasNext?: boolean;
  onPageChange?: (page: number) => void;
  history?: boolean;
  onHistoryChange?: (history: boolean) => void;
  onOpen: (sessionId: string) => void;
}

const statusPresentation: Record<
  PunchWalkthroughStatus,
  { label: string; variant: BadgeProps['variant'] }
> = {
  CREATED: { label: 'Upload incomplete', variant: 'secondary' },
  UPLOADED: { label: 'Queued', variant: 'default' },
  PROCESSING: { label: 'Processing', variant: 'default' },
  READY: { label: 'Ready', variant: 'success' },
  FAILED: { label: 'Needs attention', variant: 'destructive' },
  PUBLISHED: { label: 'Published', variant: 'success' },
  CANCELLED: { label: 'Cancelled', variant: 'secondary' },
};

const draftCount = (session: PunchWalkthroughSummary | PunchWalkthroughSession) =>
  'draft_count' in session ? session.draft_count : session.drafts.length;
const sessionActionLabel = (session: PunchWalkthroughSummary | PunchWalkthroughSession) => {
  if (session.status === 'READY') {
    return `Review ${draftCount(session)} draft${draftCount(session) === 1 ? '' : 's'}`;
  }
  if (session.status === 'FAILED') return 'View error';
  if (session.status === 'PROCESSING' || session.status === 'UPLOADED') return 'View progress';
  return 'Open';
};

const formatCreatedAt = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

export const WalkthroughDraftsPanel: React.FC<WalkthroughDraftsPanelProps> = ({
  sessions,
  isLoading = false,
  error,
  onRetry,
  count,
  page = 1,
  hasNext,
  onPageChange,
  history = false,
  onHistoryChange,
  onOpen,
}) => {
  const activeSessions = (sessions ?? []).filter((session) =>
    history
      ? session.status === 'PUBLISHED' || session.status === 'CANCELLED'
      : session.status === 'CREATED' ||
        session.status === 'UPLOADED' ||
        session.status === 'PROCESSING' ||
        session.status === 'READY' ||
        session.status === 'FAILED'
  );

  return (
    <Card className="overflow-hidden shadow-slate">
      <CardHeader className="flex-row items-center justify-between gap-3 border-b border-border pb-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">
            {history ? 'Past walkthroughs' : 'Your review queue'}
          </div>
          <CardTitle
            role="heading"
            aria-level={2}
            className="mt-1 font-display text-lg tracking-[-0.02em]"
          >
            {history ? 'Walkthrough history' : 'Active walkthroughs'}
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            {history
              ? 'Revisit published and cancelled walkthroughs.'
              : 'Resume a review or check the progress of a capture.'}
          </p>
        </div>
        <Badge className="shrink-0 whitespace-nowrap" variant="secondary">
          {isLoading || error ? '—' : (count ?? activeSessions.length)}{' '}
          {history ? 'past' : 'active'}
        </Badge>
      </CardHeader>
      <CardContent className="p-3 sm:p-4">
        <Tabs
          value={history ? 'history' : 'active'}
          onValueChange={(value) => onHistoryChange?.(value === 'history')}
        >
          {onHistoryChange && (
            <TabsList className="mb-4" aria-label="Walkthrough status">
              <TabsTrigger value="active">Active reviews</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>
          )}
          <TabsContent
            value={history ? 'history' : 'active'}
            className="mt-0"
            aria-live="polite"
            aria-busy={isLoading}
          >
            {error ? (
              <ErrorState error={error} title="Could not load walkthroughs" onRetry={onRetry} />
            ) : isLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Loading saved walkthroughs…
              </div>
            ) : activeSessions.length === 0 ? (
              <div className="rounded-[13px] border border-dashed border-border bg-muted/40 px-4 py-10 text-center">
                <Video className="mx-auto mb-3 size-6 text-muted-foreground" />
                <p className="text-sm font-semibold">
                  {history ? 'No walkthrough history yet' : 'No active walkthroughs'}
                </p>
                <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
                  {history
                    ? 'Published and cancelled walkthroughs will appear here.'
                    : 'Start a new walkthrough to capture issues. Your progress and saved reviews will appear here.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {activeSessions.map((session) => {
                  const presentation = statusPresentation[session.status];
                  const firstDraft = 'drafts' in session ? session.drafts[0] : undefined;
                  const preview =
                    firstDraft?.photos === undefined
                      ? firstDraft?.frame_url
                      : firstDraft.photos[0]?.file_url;
                  return (
                    <div
                      key={session.id}
                      className="flex items-center gap-3 rounded-[13px] border border-border bg-muted p-3"
                    >
                      <div className="grid size-14 shrink-0 place-items-center overflow-hidden rounded-[11px] bg-card text-primary">
                        {preview ? (
                          <img
                            src={preview}
                            alt="Walkthrough issue preview"
                            className="size-full object-cover"
                          />
                        ) : session.status === 'FAILED' ? (
                          <AlertTriangle className="size-5 text-destructive" />
                        ) : session.status === 'PROCESSING' || session.status === 'UPLOADED' ? (
                          <Loader2 className="size-5 animate-spin" />
                        ) : (
                          <Video className="size-5" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="truncate text-sm font-semibold">
                            {session.original_file_name || 'AI walkthrough'}
                          </div>
                          <Badge variant={presentation.variant}>{presentation.label}</Badge>
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {formatCreatedAt(session.created_at)}
                          {session.status === 'READY' &&
                            ` · ${draftCount(session)} draft${draftCount(session) === 1 ? '' : 's'}`}
                        </div>
                        {session.status === 'FAILED' && session.processing_error_message && (
                          <div className="mt-1 truncate text-xs text-destructive">
                            {session.processing_error_message}
                          </div>
                        )}
                      </div>
                      <Button
                        variant={session.status === 'READY' ? 'default' : 'outline'}
                        size="sm"
                        className="shrink-0 max-sm:px-3"
                        aria-label={sessionActionLabel(session)}
                        onClick={() => onOpen(session.id)}
                      >
                        <span className="hidden sm:inline">{sessionActionLabel(session)}</span>
                        <ChevronRight className="size-4 sm:ml-2" />
                        <span className="sr-only sm:hidden">{sessionActionLabel(session)}</span>
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            {onPageChange && (page > 1 || hasNext) && (
              <div className="mt-4 flex items-center justify-end gap-3">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page === 1 || isLoading}
                  onClick={() => onPageChange(page - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">Page {page}</span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!hasNext || isLoading}
                  onClick={() => onPageChange(page + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
